import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Lunar, Solar } from 'lunar-javascript';
import SiteSearch from './SiteSearch';
import './TopBar.css';
import storage from '../utils/LocalStorageManager';
import logger from '../utils/logger';
import trackingManager from '../services/trackingManager';
import languageManager from '../utils/languageManager';

// 搜索引擎基础配置（不包含语言相关的文本）
const SEARCH_ENGINES_CONFIG = {
  baidu: {
    searchUrl: 'https://www.baidu.com/s?wd=',
    domain: 'www.baidu.com'
  },
  google: {
    searchUrl: 'https://www.google.com/search?q=',
    domain: 'www.google.com'
  },
  sogou: {
    searchUrl: 'https://www.sogou.com/web?query=',
    domain: 'www.sogou.com'
  }
};

// 获取搜索引擎配置（包含多语言文本）
const getSearchEngine = (engineKey) => {
  return {
    ...SEARCH_ENGINES_CONFIG[engineKey],
    name: languageManager.t(`topBar.searchEngines.${engineKey}`),
    placeholder: languageManager.t(`topBar.searchPlaceholders.${engineKey}`)
  };
};

// 本地存储的键名
const STORAGE_KEY = 'searchEngine';

const TopBar = () => {
  // 直接从 Redux 获取认证状态和当前分类
  const { isLoggedIn, user } = useSelector(state => state.auth);
  const { currentCategory } = useSelector(state => state.website);
  const { autoFocusSearch, showSearch } = useSelector(state => state.settings);
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [currentEngine, setCurrentEngine] = useState(() => {
    return storage.getSearchEngine() || 'baidu';
  });
  const [showEngineSelector, setShowEngineSelector] = useState(false);
  const [showSiteSearch, setShowSiteSearch] = useState(false);
  const selectorRef = useRef(null);
  const searchContainerRef = useRef(null);
  const [engineIcons, setEngineIcons] = useState({});
  const searchInputRef = useRef(null);
  const [languageKey, setLanguageKey] = useState(0); // 用于强制重新渲染的语言键
  
  // 监听语言变化
  useEffect(() => {
    const handleLanguageChange = () => {
      setLanguageKey(prev => prev + 1); // 更新语言键以触发重新渲染
    };
    
    const unsubscribe = languageManager.addListener(handleLanguageChange);
    return () => {
      unsubscribe();
    };
  }, []);
  
  // 加载搜索引擎图标
  useEffect(() => {
    const icons = {};
    
    Object.keys(SEARCH_ENGINES_CONFIG).forEach(engine => {
      if (engine === 'google') {
        icons[engine] = 'https://i.faxianai.com/static/searchEngine/google.svg';
      } else {
        const domain = SEARCH_ENGINES_CONFIG[engine].domain;
        icons[engine] = `https://${domain}/favicon.ico`;
      }
    });
    
    setEngineIcons(icons);
  }, []);
  
  // 更新时间
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // 添加点击外部关闭下拉菜单的事件监听
  useEffect(() => {
    function handleClickOutside(event) {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setShowEngineSelector(false);
      }
      
      // 处理站内搜索下拉框的点击外部关闭
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSiteSearch(false);
      }
    }
    
    // 只有当选择器显示时才添加事件监听
    if (showEngineSelector || showSiteSearch) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEngineSelector, showSiteSearch]);

  // 监听搜索词变化，控制站内搜索显示
  useEffect(() => {
    if (searchTerm.trim().length > 0) {
      setShowSiteSearch(true);
    } else {
      setShowSiteSearch(false);
    }
  }, [searchTerm]);

  // 监听设置变更
  useEffect(() => {
    const handleSettingsChange = (event) => {
      const { key, value } = event.detail;
      if (key === 'autoFocusSearch' && value && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };

    window.addEventListener('settingsChanged', handleSettingsChange);
    return () => window.removeEventListener('settingsChanged', handleSettingsChange);
  }, []);

  // 监听分类变化，自动聚焦
  useEffect(() => {
    if (autoFocusSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [autoFocusSearch, currentCategory]);

  // 监听搜索框显示状态变化
  useEffect(() => {
    document.body.setAttribute('data-search-visible', showSearch.toString());
  }, [showSearch]);

  // 格式化时间
  const formatTime = () => {
    const hours = currentTime.getHours().toString().padStart(2, '0');
    const minutes = currentTime.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // 格式化日期
  const formatDate = () => {
    const month = (currentTime.getMonth() + 1).toString().padStart(2, '0');
    const day = currentTime.getDate().toString().padStart(2, '0');
    const weekDays = ['日', '一', '二', '三', '四', '五', '六'];
    const weekDay = weekDays[currentTime.getDay()];
    
    // 正式的农历日期转换
    try {
      const solar = Solar.fromDate(currentTime);
      const lunar = solar.getLunar();
      
      // 获取农历月份和日期
      const lunarMonth = lunar.getMonthInChinese();
      const lunarDay = lunar.getDayInChinese();
      
      // 如果是农历初一，显示月份，否则只显示日期
      const lunarDate = lunarDay === '初一' ? `${lunarMonth}${lunarDay}` : lunarDay;
      
      return `${month}月${day}日 星期${weekDay} ${lunarDate}`;
    } catch (error) {
      console.warn('农历转换失败，使用默认显示:', error);
      // 如果农历转换失败，回退到简单显示
      return `${month}月${day}日 星期${weekDay}`;
    }
  };

  // 执行搜索
  const performSearch = () => {
    if (searchTerm.trim()) {
      const engine = getSearchEngine(currentEngine);
      
      // 上报搜索埋点
      trackingManager.reportSearch(searchTerm, `${engine.name}搜索`);
      
      window.open(engine.searchUrl + encodeURIComponent(searchTerm), '_blank');
    }
  };

  // 处理搜索引擎切换
  const handleEngineChange = (engine) => {
    setCurrentEngine(engine);
    storage.setSearchEngine(engine);
    setShowEngineSelector(false);
  };

  // 处理输入框键盘事件
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  };

  // 清空搜索内容
  const clearSearch = () => {
    setSearchTerm('');
    setShowSiteSearch(false);
  };

  // 处理站内搜索项点击
  const handleSiteSearchItemClick = (item) => {
    // 上报搜索结果点击埋点
    trackingManager.reportEvent('NAV_SEARCH_RESULT_CLICK', {
      content: item.url || '',
      p1: item.name || '',
      p2: searchTerm,
      p3: 'site_search'
    });
    
    // 清空搜索框并隐藏搜索结果
    setSearchTerm('');
    setShowSiteSearch(false);
  };

  // 图标加载失败时的处理
  const handleIconError = (engine) => {
    // 使用备用图标
    const backupIcons = {
      baidu: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path d="M8.31 1.2c1.24 0 2.25 1.01 2.25 2.25S9.55 5.7 8.31 5.7s-2.25-1.01-2.25-2.25S7.07 1.2 8.31 1.2zM3.39 2.74c.68 0 1.23.55 1.23 1.23s-.55 1.23-1.23 1.23-1.23-.55-1.23-1.23.55-1.23 1.23-1.23zm9.84.02c.68 0 1.23.55 1.23 1.23s-.55 1.23-1.23 1.23-1.23-.55-1.23-1.23.55-1.23 1.23-1.23zM3.69 7.21c.71 0 1.29.58 1.29 1.29 0 .71-.58 1.29-1.29 1.29s-1.29-.58-1.29-1.29c0-.71.58-1.29 1.29-1.29zm9.24.03c.71 0 1.29.58 1.29 1.29 0 .71-.58 1.29-1.29 1.29s-1.29-.58-1.29-1.29c0-.71.58-1.29 1.29-1.29zm-4.62.36c2.03 0 3.68 1.65 3.68 3.68s-1.65 3.68-3.68 3.68-3.68-1.65-3.68-3.68 1.65-3.68 3.68-3.68z" fill="%23306CFF"/></svg>',
      google: 'https://i.faxianai.com/static/searchEngine/google.svg',
      sogou: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path d="M8 1.33C6.99 1.33 6.2 2 6 2.93c-.2 1.2-.27 2.4-.27 3.73v.67H4.6c-.53 0-.93.4-.93.94v.4c0 .13.07.33.13.47.13.2.33.47.67.93.53 1.07.53 1.27.27 2-.33 1-.07 1.67.67 2.13.6.33.67.33 3.6.33s3.07 0 3.6-.33c.73-.4 1-1.2.67-2.07-.33-.87-.27-1.07.27-2 .27-.53.53-.93.67-1.07.07-.07.13-.27.13-.53 0-.53-.13-.87-.4-1.07-.33-.2-.47-.2-2.67-.2H8.27v-.73c0-1.67-.07-2-.27-2.4C7.67 1.6 7.13 1.33 8 1.33z" fill="%23F36D45"/></svg>'
    };
    
    setEngineIcons(prevIcons => ({
      ...prevIcons,
      [engine]: backupIcons[engine]
    }));
  };

  return (
    <div className="top-bar">
      <div className="time-display">
        <div className="current-time bold">{formatTime()}</div>
        <div className="current-date">{formatDate()}</div>
      </div>
      
      <div className="search-container" ref={searchContainerRef} style={{ display: showSearch ? 'flex' : 'none' }}>
        <div className="search-engine-area" ref={selectorRef}>
          <div 
            className="search-engine-selector" 
            onClick={() => setShowEngineSelector(!showEngineSelector)}
          >
            {engineIcons[currentEngine] ? (
              <img 
                src={engineIcons[currentEngine]} 
                alt={getSearchEngine(currentEngine).name}
                width="18" 
                height="18"
                onError={() => handleIconError(currentEngine)}
                className="engine-favicon"
              />
            ) : (
              <div className="engine-favicon-placeholder" />
            )}
            <div className="engine-dropdown-icon">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9L12 15L18 9" fill="#666" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          
          {showEngineSelector && (
            <div className="engine-dropdown">
              {Object.keys(SEARCH_ENGINES_CONFIG).map(engine => (
                <div 
                  key={engine} 
                  className={`engine-option ${engine === currentEngine ? 'active' : ''}`}
                  onClick={() => handleEngineChange(engine)}
                >
                  <span className="engine-icon">
                    {engineIcons[engine] ? (
                      <img 
                        src={engineIcons[engine]} 
                        alt={getSearchEngine(engine).name}
                        width="18" 
                        height="18"
                        onError={() => handleIconError(engine)}
                        className="engine-favicon"
                      />
                    ) : (
                      <div className="engine-favicon-placeholder" />
                    )}
                  </span>
                  <span className="engine-name">{getSearchEngine(engine).name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <input 
          ref={searchInputRef}
          type="text" 
          placeholder={getSearchEngine(currentEngine).placeholder}
          className="search-input" 
          style={{ pointerEvents: 'auto' }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        
        {searchTerm && (
          <div className="clear-search" onClick={clearSearch}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
        
        <div className="search-button" onClick={performSearch}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 21L16.65 16.65" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        {/* 站内搜索结果 */}
        <SiteSearch
          searchTerm={searchTerm}
          visible={showSiteSearch}
          onItemClick={handleSiteSearchItemClick}
        />
      </div>
    </div>
  );
};

export default TopBar; 